/**
 * The dev-vs-prod data seam.
 *
 * `isPreviewMode()` mirrors main.tsx's `shouldBypassClerk` rule exactly: when
 * there is NO Clerk publishable key AND we're in a Vite dev build (or on an
 * explicit /preview/* route), the app renders WITHOUT a ClerkProvider. In that
 * mode the hooks must NOT touch Clerk (`useAuth` asserts a provider and throws)
 * and must serve fixtures instead of hitting the authenticated API.
 *
 * In a production build with a Clerk key present, this is `false`, the hooks
 * call the real `/api/professional/*` endpoints with a Clerk session token.
 *
 * The value is computed once from env (stable across the session), so a hook
 * may branch on it at the top without violating the rules of hooks.
 */
import { getBrowserPathname, isDevPreviewPath } from "./dev-preview";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * True when the app is running without Clerk (preview). Computed once at module
 * load so it never changes between renders.
 */
export const IS_PREVIEW: boolean =
  !CLERK_KEY &&
  (import.meta.env.DEV || isDevPreviewPath(getBrowserPathname()));

/** Stable accessor (function form for call-sites that prefer it). */
export function isPreviewMode(): boolean {
  return IS_PREVIEW;
}

/**
 * Reads a query-string param from the current browser location — an
 * IS_PREVIEW-only seam that lets e2e specs (marketplace-billing.spec.ts)
 * force a preview fixture into a specific variant via `page.goto("/settings
 * ?billing=card")` without a backend or real Stripe/network call. Mirrors
 * `useCopilotPreviewFixture`'s `?copilotDemo=<variant>` idiom (read
 * URLSearchParams, IS_PREVIEW-guarded, no-op otherwise).
 *
 * Returns `null` outside IS_PREVIEW, when `window` is unavailable (SSR/unit
 * tests), or when the param is absent — callers fall back to the default
 * preview fixture in all of those cases.
 */
export function previewQueryParam(name: string): string | null {
  if (!IS_PREVIEW || typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}
